import {
  ButtonsWrapper,
  ExternalWrapper,
} from '@components/Steps/Steps.styles';
import { LuxButton, LuxTypography, Stack, useTheme } from '@gemini/fe-ds-core';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { Route, Switch, useHistory } from 'react-router-dom';
import ReschedulePage, { useRescheduleMeta } from './ReschedulePage';
import RescheduleScheduleExamPage from './RescheduleScheduleExamPage';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import AppointmentErrorModal from '@components/AppointmentErrorModal/AppointmentErrorModal';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import { conditionallyApplyUpperCase } from '@utils/functions';
import { isEmbeddedIntegrationMode } from '@utils/enums/IntegrationModes.enum';
import ReservationTimer from '@components/CalendarPage/ReservationTimer';
import { usePatientStore } from '@store/patientStore';

enum RescheduleSteps {
  RESCHEDULE,
  RESCHEDULE_SCHEDULE_EXAM,
  RESCHEDULE_CONFIRMED,
}

const RescheduleFlow = () => {
  // BEGIN breakpoint change listeners
  const theme = useTheme();
  const brandName = useOnlineBookingStore(state => state.brandName);
  const [breakpoint, setBreakpoint] = useState<'mobile' | 'desktop'>(
    window.innerWidth > MobileBreakpoint ? 'desktop' : 'mobile',
  );

  window.addEventListener('resize', () => {
    if (breakpoint === 'mobile' && window.innerWidth > MobileBreakpoint)
      setBreakpoint('desktop');
    if (breakpoint === 'desktop' && window.innerWidth <= MobileBreakpoint)
      setBreakpoint('mobile');
  });
  // END breakpoint change listeners

  const { t } = useTranslation();
  const { Urls } = useUrlsContext();

  const history = useHistory();
  const [rescheduleStep, setRescheduleStep] = useState<RescheduleSteps>(
    RescheduleSteps.RESCHEDULE,
  );
  const setShouldSave = useOnlineBookingStore(state => state.setShouldSave);
  const [enableButton, setEnableButton] = useState(true);
  const [showAppointmentErrorModal, setShowAppointmentErrorModal] =
    useState(false);
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);

  const { reschedulable, appointmentStatus } =
    useRescheduleMeta(integrationMode);

  const showBackButton = useMemo(() => rescheduleStep > 0, [rescheduleStep]);

  const patients = usePatientStore(state => state.patients);
  const timer = useMemo(
    () => patients.some(patient => patient?.extraInfoScheduleExam?.timer),
    [patients],
  );

  const handleBack = () => {
    switch (rescheduleStep) {
      case RescheduleSteps.RESCHEDULE_SCHEDULE_EXAM:
        history.push(`${Urls.RESCHEDULE}`);
        break;
      default:
        console.log('##RESCHEDULE STEP: Unknown step ->', rescheduleStep);
    }
  };

  const handleNextStep = useCallback(() => {
    if (!reschedulable) {
      setShowAppointmentErrorModal(true);
      return;
    }
    switch (rescheduleStep) {
      case RescheduleSteps.RESCHEDULE:
        history.push(`${Urls.RESCHEDULE_SCHEDULE_EXAM}`);
        break;
      case RescheduleSteps.RESCHEDULE_SCHEDULE_EXAM:
        setShouldSave(true);
        history.push(`${Urls.RESCHEDULE_CONFIRMED}`);
        break;
      default:
        console.log('##RESCHEDULE STEP: Unknown step ->', rescheduleStep);
    }
  }, [rescheduleStep, reschedulable]);

  const routePath = useMemo(
    () =>
      isEmbeddedIntegrationMode(integrationMode)
        ? new URL((location.hash || '#').slice(1) || '/', location.origin)
            .pathname
        : location.pathname,
    [integrationMode, location.hash, location.pathname],
  );

  useEffect(() => {
    switch (routePath) {
      case Urls.RESCHEDULE:
        setRescheduleStep(RescheduleSteps.RESCHEDULE);
        setEnableButton(true);
        break;
      case Urls.RESCHEDULE_SCHEDULE_EXAM:
        setRescheduleStep(RescheduleSteps.RESCHEDULE_SCHEDULE_EXAM);
        break;
      case Urls.RESCHEDULE_CONFIRMED:
        setRescheduleStep(RescheduleSteps.RESCHEDULE_CONFIRMED);
        break;
      default:
        console.warn('##RESCHEDULE STEP: Unknown path ->', routePath);
    }
  }, [routePath]);

  return (
    <>
      <ExternalWrapper
        backgroundColor={
          brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
        }
      >
        <Switch>
          <Route exact path={Urls.RESCHEDULE}>
            {showAppointmentErrorModal ? (
              <AppointmentErrorModal
                statusCode={appointmentStatus}
                actionCode="reschedule"
              />
            ) : (
              <ReschedulePage />
            )}
          </Route>
          <Route exact path={Urls.RESCHEDULE_SCHEDULE_EXAM}>
            <RescheduleScheduleExamPage setEnableButton={setEnableButton} />
          </Route>
        </Switch>
      </ExternalWrapper>
      {!showAppointmentErrorModal && (
        <ButtonsWrapper step={rescheduleStep} flexDirection="column">
          {breakpoint === 'mobile' && timer && <ReservationTimer />}
          <Stack
            direction="row"
            justifyContent={rescheduleStep > 0 ? 'space-between' : 'flex-end'}
            width="100%"
          >
            {showBackButton && (
              <LuxButton
                variant="ghost"
                // iconStart="ChevronLeftIcon"
                onClick={handleBack}
                style={{
                  width: breakpoint === 'mobile' ? '20%' : 'unset',
                }}
              >
                <LuxTypography
                  variant="textMedium"
                  fontWeight={brandName && '700!important'}
                >
                  {t('generalCta.back')}
                </LuxTypography>
              </LuxButton>
            )}
            {breakpoint === 'desktop' ? (
              <div
                style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
              >
                {timer && <ReservationTimer />}
                <LuxButton
                  onClick={handleNextStep}
                  disabled={!enableButton}
                  iconStart={
                    rescheduleStep === RescheduleSteps.RESCHEDULE
                      ? 'CalendarScheduleIcon'
                      : undefined
                  }
                >
                  <LuxTypography
                    variant={'textMdRegular'}
                    fontWeight={brandName && '700!important'}
                  >
                    {rescheduleStep === RescheduleSteps.RESCHEDULE
                      ? conditionallyApplyUpperCase(
                          t('rescheduleAppointment.rescheduleCTA'),
                          brandName,
                        )
                      : conditionallyApplyUpperCase(
                          t('rescheduleAppointment.continueCTA'),
                          brandName,
                        )}
                  </LuxTypography>
                </LuxButton>
              </div>
            ) : (
              <LuxButton
                onClick={handleNextStep}
                disabled={!enableButton}
                iconStart={
                  rescheduleStep === RescheduleSteps.RESCHEDULE
                    ? 'CalendarScheduleIcon'
                    : undefined
                }
              >
                <LuxTypography
                  variant={'textMdRegular'}
                  fontWeight={brandName && '700!important'}
                >
                  {rescheduleStep === RescheduleSteps.RESCHEDULE
                    ? conditionallyApplyUpperCase(
                        t('rescheduleAppointment.rescheduleCTA'),
                        brandName,
                      )
                    : conditionallyApplyUpperCase(
                        t('rescheduleAppointment.continueCTA'),
                        brandName,
                      )}
                </LuxTypography>
              </LuxButton>
            )}
          </Stack>
        </ButtonsWrapper>
      )}
    </>
  );
};

export default RescheduleFlow;
